const SALES_BENCHMARKS = {
  hook:         { score: 7, label: 'Top performers hook in <15 sec', src: 'Gong' },
  clarity:      { score: 7, label: 'Value prop clear in <30 sec', src: 'RAIN Group' },
  objections:   { score: 6, label: 'Answer objections with a question', src: 'Gong' },
  articulation: { score: 7, label: 'No filler words; confident pacing', src: 'RAIN Group' },
  listening:    { score: 8, label: '57% listen vs 43% talk ratio', src: 'Gong' },
  close:        { score: 6, label: "64% of reps never ask for a next step", src: 'HubSpot' },
};

const INTERVIEW_BENCHMARKS = {
  communication:   { score: 7, label: 'Top candidates use <3 filler words per minute', src: 'LinkedIn Hiring' },
  relevance:       { score: 7, label: '87% of hiring managers reject candidates who dodge questions', src: 'SHRM' },
  examples:        { score: 6, label: 'STAR-format answers increase hire rate by 40%', src: 'HBR' },
  composure:       { score: 7, label: 'Candidates who pause before answering score 30% higher', src: 'Korn Ferry' },
  enthusiasm:      { score: 8, label: '#1 trait hiring managers cite after skills', src: 'LinkedIn 2024' },
  professionalism: { score: 7, label: 'First 30 seconds sets 80% of interviewer impression', src: 'Princeton' },
};

const SALES_DIMS     = ['hook','clarity','objections','articulation','listening','close'];
const INTERVIEW_DIMS = ['communication','relevance','examples','composure','enthusiasm','professionalism'];
const SALES_LABELS   = { hook:'Hook', clarity:'Clarity', objections:'Objections', articulation:'Articulation', listening:'Listening', close:'Close' };
const INTERVIEW_LABELS = { communication:'Communication', relevance:'Relevance', examples:'Examples', composure:'Composure', enthusiasm:'Enthusiasm', professionalism:'Professionalism' };

function scoreColor(s) { return s >= 7 ? '#16A34A' : s >= 5 ? '#D97706' : '#DC2626'; }
function scoreBg(s)    { return s >= 7 ? '#F0FDF4' : s >= 5 ? '#FFFBEB' : '#FEF2F2'; }

export default function Scorecard({ feedback, mode = 'sales' }) {
  if (!feedback || feedback.error) return null;
  const { scores, what_worked, fix_immediately, summary, talk_ratio } = feedback;

  const isInterview = mode === 'interview';
  const DIMS       = isInterview ? INTERVIEW_DIMS : SALES_DIMS;
  const LABELS     = isInterview ? INTERVIEW_LABELS : SALES_LABELS;
  const BENCHMARKS = isInterview ? INTERVIEW_BENCHMARKS : SALES_BENCHMARKS;

  const validDims = DIMS.filter(k => scores && scores[k]);
  if (validDims.length === 0) return null;

  const avg = +(validDims.reduce((a, k) => a + scores[k].score, 0) / validDims.length).toFixed(1);

  const gradeText = isInterview
    ? (avg >= 8 ? '🟢 Strong — ready to interview' : avg >= 6 ? '🟡 Getting there — work on the fixes' : avg >= 4 ? '🟠 Needs practice — keep going' : '🔴 Early stage — focus on the basics')
    : (avg >= 8 ? '🟢 Strong — close to pitch-ready' : avg >= 6 ? '🟡 Getting there — fix the weak areas' : avg >= 4 ? '🟠 Needs work' : '🔴 Early stage — keep practicing');

  return (
    <div style={S.wrap}>
      <div style={{ ...S.modeBadge, ...(isInterview ? S.modeBadgeInterview : S.modeBadgeSales) }}>
        {isInterview ? '🎯 Interview Scorecard' : '📞 Sales Call Scorecard'}
      </div>

      <div style={S.header}>
        <div>
          <div style={{ ...S.avg, color: scoreColor(avg) }}>
            {avg}<span style={S.avgSub}>/10</span>
          </div>
          <div style={S.grade}>{gradeText}</div>
        </div>
        {talk_ratio != null && (
          <div style={S.ratioBox}>
            <div style={S.ratioLabel}>Talk ratio</div>
            <div style={S.ratioTrack}>
              <div style={{ ...S.ratioFill, width: `${talk_ratio}%`, background: talk_ratio > 60 ? '#EF4444' : talk_ratio < 20 ? '#F59E0B' : '#22C55E' }} />
              <div style={S.ratioTarget} />
            </div>
            <div style={S.ratioNote}>{talk_ratio}% you · {isInterview ? 'aim ~40% — listen more' : 'target ≤43% (Gong)'}</div>
          </div>
        )}
      </div>

      <div style={S.dims}>
        {validDims.map(k => {
          const { score, critique } = scores[k];
          const bench = BENCHMARKS[k]?.score || 7;
          const pct = v => `${(v / 10 * 100).toFixed(0)}%`;
          const diff = score - bench;
          return (
            <div key={k} style={S.dimBlock}>
              <div style={S.dimRow}>
                <div style={S.dimName}>{LABELS[k]}</div>
                <div style={S.barWrap}>
                  <div style={S.barBg} />
                  <div style={{ ...S.barBench, width: pct(bench) }} />
                  <div style={{ ...S.barYou, width: pct(score), background: scoreColor(score) }} />
                  <div style={{ ...S.barMarker, left: pct(bench) }} />
                </div>
                <div style={{ ...S.dimScore, color: scoreColor(score), background: scoreBg(score) }}>{score}</div>
                <div style={{ ...S.dimVs, color: diff > 0 ? '#16A34A' : diff < 0 ? '#DC2626' : '#9CA3AF' }}>
                  {diff > 0 ? `+${diff}` : diff === 0 ? '—' : diff}
                </div>
              </div>
              <div style={S.critique}>{critique}</div>
              <div style={S.benchNote}>{BENCHMARKS[k]?.label} · {BENCHMARKS[k]?.src}</div>
            </div>
          );
        })}
      </div>

      <div style={S.cards}>
        <div style={S.workedCard}>
          <div style={S.workedLabel}>✓ {isInterview ? 'Strongest moment' : 'What worked'}</div>
          <div style={S.workedText}>{what_worked}</div>
        </div>
        <div style={S.fixCard}>
          <div style={S.fixLabel}>⚠ {isInterview ? 'Work on these' : 'Fix immediately'}</div>
          {fix_immediately.map((f, i) => <div key={i} style={S.fixItem}>→ {f}</div>)}
        </div>
      </div>
      <div style={S.summaryCard}>
        <div style={S.summaryLabel}>{isInterview ? 'Coach assessment' : 'Coach summary'}</div>
        <div style={S.summaryText}>"{summary}"</div>
      </div>
    </div>
  );
}

const S = {
  wrap: { padding: '20px 24px', borderBottom: '1px solid #E5E7EB' },
  modeBadge: { display: 'inline-block', fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 999, marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.06em' },
  modeBadgeInterview: { background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' },
  modeBadgeSales: { background: '#F0FDF4', color: '#15803D', border: '1px solid #BBF7D0' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  avg: { fontSize: 36, fontWeight: 800, fontFamily: 'JetBrains Mono, monospace', lineHeight: 1 },
  avgSub: { fontSize: 16, color: '#9CA3AF', fontFamily: 'Inter, sans-serif' },
  grade: { fontSize: 13, color: '#6B7280', marginTop: 4 },
  ratioBox: { textAlign: 'right' },
  ratioLabel: { fontSize: 11, color: '#9CA3AF', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' },
  ratioTrack: { width: 160, height: 6, background: '#E5E7EB', borderRadius: 3, position: 'relative', overflow: 'visible' },
  ratioFill: { height: '100%', borderRadius: 3 },
  ratioTarget: { position: 'absolute', top: -3, left: '43%', width: 2, height: 12, background: '#2563EB', opacity: 0.5, borderRadius: 1 },
  ratioNote: { fontSize: 11, color: '#9CA3AF', marginTop: 4 },
  dims: { display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 },
  dimBlock: {},
  dimRow: { display: 'flex', alignItems: 'center', gap: 10 },
  dimName: { width: 120, fontSize: 12, fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 },
  barWrap: { flex: 1, height: 20, position: 'relative' },
  barBg: { position: 'absolute', top: '50%', transform: 'translateY(-50%)', width: '100%', height: 6, background: '#F1F3F7', borderRadius: 3 },
  barBench: { position: 'absolute', top: '50%', transform: 'translateY(-50%)', height: 6, background: '#E5E7EB', borderRadius: 3 },
  barYou: { position: 'absolute', top: '50%', transform: 'translateY(-50%)', height: 6, borderRadius: 3, transition: 'width 0.5s ease' },
  barMarker: { position: 'absolute', top: 2, width: 2, height: 16, background: '#2563EB', opacity: 0.35, borderRadius: 1 },
  dimScore: { width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', flexShrink: 0 },
  dimVs: { width: 32, fontSize: 12, fontWeight: 600, flexShrink: 0, textAlign: 'right' },
  critique: { fontSize: 12, color: '#6B7280', marginTop: 3, paddingLeft: 128, lineHeight: 1.5 },
  benchNote: { fontSize: 11, color: '#9CA3AF', paddingLeft: 128, marginTop: 2, fontStyle: 'italic' },
  cards: { display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' },
  workedCard: { flex: '1 1 200px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: '14px 16px' },
  workedLabel: { fontSize: 11, fontWeight: 700, color: '#15803D', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 },
  workedText: { fontSize: 13, color: '#166534', lineHeight: 1.5 },
  fixCard: { flex: '1 1 200px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '14px 16px' },
  fixLabel: { fontSize: 11, fontWeight: 700, color: '#B91C1C', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 },
  fixItem: { fontSize: 13, color: '#991B1B', lineHeight: 1.5, marginBottom: 4 },
  summaryCard: { background: '#F8F9FB', border: '1px solid #E5E7EB', borderRadius: 10, padding: '14px 16px' },
  summaryLabel: { fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 },
  summaryText: { fontSize: 13, color: '#374151', lineHeight: 1.6, fontStyle: 'italic' },
};
